import { useCallback, useEffect, useMemo } from "react";
import { Divider } from "antd";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import { AnswerResponse } from "@/redux/survey/types";
import { SelectionToolbarProps } from "@/features/survey-views/form/types";
import { useLazyGetSurveyByIdQuery } from "@/redux/service";
import { SharingRights, SurveyType } from "@/shared/utils/constants";
import { SurveyViewPublicationDestination } from "@/redux/survey-views/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

import "../styles.css";

const SelectionToolbar = ({ form, selectable }: SelectionToolbarProps) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const currentOrganizationId = useOrganizationId();
  const [getSurveyById, surveyResponse] = useLazyGetSurveyByIdQuery();

  const surveyId = useWatch({
    control: form.control,
    name: "survey",
  });
  const surveyViewPublicationDestination = useWatch({
    control: form.control,
    name: "config.publicationDestination",
  });
  const currentSelectedAnswers = useWatch({
    control: form.control,
    name: "answers",
  });

  const totals = useMemo(
    () => ({
      nonEvidenceAnswers: selectable.answersWithoutBlocks.length,
      evidenceAnswers: selectable.answersWithBlocks.length,
      blocks: selectable.totalBlocks,
      publicBlocks:
        selectable.totalBlocksPerSharingRight[SharingRights.PUBLIC.value] ?? 0,
      prmeBlocks:
        selectable.totalBlocksPerSharingRight[
          SharingRights.PRME_COMMUNITY.value
        ] ?? 0,
      privateBlocks:
        selectable.totalBlocksPerSharingRight[SharingRights.PRIVATE.value] ?? 0,
    }),
    [selectable],
  );

  useEffect(() => {
    if (surveyId) {
      getSurveyById({
        surveyId,
        filters: { organization: currentOrganizationId },
      });
    }
  }, [surveyId, currentOrganizationId]);

  const updateFormAnswersForBlocks = useCallback(
    (
      answersWithBlocks: AnswerResponse[],
      sharingRightFilter: string,
      checked: boolean,
    ) => {
      const currentAnswerMap = new Map(
        currentSelectedAnswers.map((answer) => [answer.answer, answer]),
      );

      answersWithBlocks.forEach((answer) => {
        const filteredBlocks = answer.attachedBlocks
          .filter((block) => block.sharingRights === sharingRightFilter)
          .map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          }));

        if (filteredBlocks.length === 0) return;

        const existingAnswer = currentAnswerMap.get(answer.id);

        if (checked) {
          if (existingAnswer) {
            const existingBlockIds = new Set(
              existingAnswer.attachedBlocks.map((b) => b.id),
            );
            const newBlocks = filteredBlocks.filter(
              (block) => !existingBlockIds.has(block.id),
            );

            if (newBlocks.length > 0) {
              currentAnswerMap.set(answer.id, {
                ...existingAnswer,
                attachedBlocks: [
                  ...existingAnswer.attachedBlocks,
                  ...newBlocks,
                ],
              });
            }
          } else {
            currentAnswerMap.set(answer.id, {
              answer: answer.id,
              question: answer.question,
              attachedBlocks: filteredBlocks,
            });
          }
        } else {
          if (existingAnswer) {
            const blocksToRemove = new Set(filteredBlocks.map((b) => b.id));
            const updatedBlocks = existingAnswer.attachedBlocks.filter(
              (block) => {
                const shouldRemove = blocksToRemove.has(block.id);

                if (
                  shouldRemove &&
                  block.sharingRights === SharingRights.PUBLIC.value &&
                  surveyViewPublicationDestination ===
                    SurveyViewPublicationDestination.WEBSITE
                ) {
                  return true;
                }

                return !shouldRemove;
              },
            );

            if (updatedBlocks.length > 0) {
              currentAnswerMap.set(answer.id, {
                ...existingAnswer,
                attachedBlocks: updatedBlocks,
              });
            } else {
              currentAnswerMap.delete(answer.id);
            }
          }
        }
      });

      form.setValue("answers", Array.from(currentAnswerMap.values()));
    },
    [surveyViewPublicationDestination, currentSelectedAnswers, form],
  );

  const getIsNonEvidenceDisabled = useMemo(
    () => totals.nonEvidenceAnswers === 0,
    [totals],
  );

  const getIsEvidenceDisabled = useMemo(
    () => totals.evidenceAnswers === 0,
    [totals],
  );

  const getIsPublicEvidenceDisabled = useMemo(
    () =>
      surveyViewPublicationDestination ===
        SurveyViewPublicationDestination.WEBSITE || totals.publicBlocks === 0,
    [surveyViewPublicationDestination, totals],
  );

  const getIsPrmeCommunityEvidenceDisabled = useMemo(
    () => totals.prmeBlocks === 0,
    [totals],
  );

  const getIsPrivateEvidenceDisabled = useMemo(
    () => totals.privateBlocks === 0,
    [totals],
  );

  const getSelectionState = useCallback(
    (type: "nonEvidence" | "evidence" | "public" | "prme" | "private") => {
      const selectedBlockCounts = currentSelectedAnswers.reduce(
        (acc, answer) => {
          answer.attachedBlocks.forEach((block) => {
            if (block.sharingRights === SharingRights.PUBLIC.value)
              acc.public++;
            if (block.sharingRights === SharingRights.PRME_COMMUNITY.value)
              acc.prme++;
            if (block.sharingRights === SharingRights.PRIVATE.value)
              acc.private++;
          });
          return acc;
        },
        { public: 0, prme: 0, private: 0 },
      );

      switch (type) {
        case "nonEvidence": {
          const selectedNonEvidenceCount = currentSelectedAnswers.filter(
            (answer) => answer.attachedBlocks.length === 0,
          ).length;
          return (
            totals.nonEvidenceAnswers > 0 &&
            selectedNonEvidenceCount >= totals.nonEvidenceAnswers
          );
        }

        case "evidence": {
          // Evidence is selected when all available evidence blocks are selected
          const totalSelectedBlocks =
            selectedBlockCounts.public +
            selectedBlockCounts.prme +
            selectedBlockCounts.private;
          return totals.blocks > 0 && totalSelectedBlocks >= totals.blocks;
        }

        case "public":
          return (
            totals.publicBlocks > 0 &&
            selectedBlockCounts.public >= totals.publicBlocks
          );

        case "prme":
          return (
            totals.prmeBlocks > 0 &&
            selectedBlockCounts.prme >= totals.prmeBlocks
          );

        case "private":
          return (
            totals.privateBlocks > 0 &&
            selectedBlockCounts.private >= totals.privateBlocks
          );

        default:
          return false;
      }
    },
    [totals, currentSelectedAnswers],
  );

  const getIsNonEvidenceSelected = useMemo(() => {
    if (getIsNonEvidenceDisabled) return false;
    return getSelectionState("nonEvidence");
  }, [getSelectionState, getIsNonEvidenceDisabled]);

  const getIsEvidenceSelected = useMemo(() => {
    if (getIsEvidenceDisabled) return false;
    return getSelectionState("evidence");
  }, [getSelectionState, getIsEvidenceDisabled]);

  const getIsPublicEvidenceSelected = useMemo(() => {
    if (
      surveyViewPublicationDestination ===
      SurveyViewPublicationDestination.WEBSITE
    )
      return true;
    if (getIsPublicEvidenceDisabled) return false;
    return getSelectionState("public");
  }, [
    surveyViewPublicationDestination,
    getSelectionState,
    getIsPublicEvidenceDisabled,
  ]);

  const getIsPrmeCommunityEvidenceSelected = useMemo(() => {
    if (getIsPrmeCommunityEvidenceDisabled) return false;
    return getSelectionState("prme");
  }, [getSelectionState, getIsPrmeCommunityEvidenceDisabled]);

  const getIsPrivateEvidenceSelected = useMemo(() => {
    if (getIsPrivateEvidenceDisabled) return false;
    return getSelectionState("private");
  }, [getSelectionState, getIsPrivateEvidenceDisabled]);

  const updateFormAnswers = useCallback(
    (answersData: AnswerResponse[], checked: boolean) => {
      if (checked) {
        const currentAnswerMap = new Map(
          currentSelectedAnswers.map((answer) => [answer.answer, answer]),
        );

        answersData.forEach((answer) => {
          const newBlocks = answer.attachedBlocks.map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          }));

          const existingAnswer = currentAnswerMap.get(answer.id);

          if (existingAnswer) {
            const existingBlockIds = new Set(
              existingAnswer.attachedBlocks.map((b) => b.id),
            );
            const blocksToAdd = newBlocks.filter(
              (block) => !existingBlockIds.has(block.id),
            );

            if (blocksToAdd.length > 0) {
              currentAnswerMap.set(answer.id, {
                ...existingAnswer,
                attachedBlocks: [
                  ...existingAnswer.attachedBlocks,
                  ...blocksToAdd,
                ],
              });
            }
          } else {
            currentAnswerMap.set(answer.id, {
              answer: answer.id,
              question: answer.question,
              attachedBlocks: newBlocks,
            });
          }
        });

        form.setValue("answers", Array.from(currentAnswerMap.values()));
      } else {
        const answerIdsToRemove = new Set(
          answersData.map((answer) => answer.id),
        );

        const updatedAnswers = currentSelectedAnswers.reduce(
          (acc, answer) => {
            if (!answerIdsToRemove.has(answer.answer)) {
              acc.push(answer);
              return acc;
            }

            // For website surveys, keep public blocks when removing other blocks
            if (
              surveyViewPublicationDestination ===
              SurveyViewPublicationDestination.WEBSITE
            ) {
              const publicBlocks = answer.attachedBlocks.filter(
                (block) => block.sharingRights === SharingRights.PUBLIC.value,
              );

              if (publicBlocks.length > 0) {
                acc.push({
                  ...answer,
                  attachedBlocks: publicBlocks,
                });
              }
            }

            return acc;
          },
          [] as typeof currentSelectedAnswers,
        );
        form.setValue("answers", updatedAnswers);
      }
    },
    [currentSelectedAnswers, surveyViewPublicationDestination, form],
  );

  const handleSelectNonEvidenceAnswers = useCallback(
    (checked: boolean) =>
      updateFormAnswers(selectable.answersWithoutBlocks, checked),
    [updateFormAnswers, selectable.answersWithoutBlocks],
  );

  const handleSelectEvidenceAnswers = useCallback(
    (checked: boolean) =>
      updateFormAnswers(selectable.answersWithBlocks, checked),
    [updateFormAnswers, selectable.answersWithBlocks],
  );

  const handleSelectPublicEvidenceAnswers = useCallback(
    (checked: boolean) =>
      updateFormAnswersForBlocks(
        selectable.answersWithBlocks,
        SharingRights.PUBLIC.value,
        checked,
      ),
    [updateFormAnswersForBlocks, selectable.answersWithBlocks],
  );

  const handleSelectPrmeCommunityEvidenceAnswers = useCallback(
    (checked: boolean) =>
      updateFormAnswersForBlocks(
        selectable.answersWithBlocks,
        SharingRights.PRME_COMMUNITY.value,
        checked,
      ),
    [updateFormAnswersForBlocks, selectable.answersWithBlocks],
  );

  const handleSelectPrivateEvidenceAnswers = useCallback(
    (checked: boolean) =>
      updateFormAnswersForBlocks(
        selectable.answersWithBlocks,
        SharingRights.PRIVATE.value,
        checked,
      ),
    [updateFormAnswersForBlocks, selectable.answersWithBlocks],
  );

  const handleSelectAll = useCallback(
    () => updateFormAnswers(selectable.selectableAnswers, true),
    [updateFormAnswers, selectable.selectableAnswers],
  );

  return (
    <div className="survey-view-selection-toolbar flex flex-col gap-300 full-width items-start">
      <h4 className="header-s-medium">
        {t("surveyViews.labels.formContentLabel")}
      </h4>
      {currentUser && (
        <>
          <BaseButton onClick={handleSelectAll}>
            {t("surveyViews.labels.selectAll")}
          </BaseButton>

          <Divider style={{ margin: "12px 0" }} />

          <div className="flex flex-col gap-300">
            <h5 className="body-m-medium">
              {t("surveyViews.labels.selectionByAnswerType")}
            </h5>

            <div className="flex flex-col gap-250">
              {surveyResponse.data?.type === SurveyType.SIP.value && (
                <BaseCheckbox
                  label={t("surveyViews.labels.nonEvidenceAnswers")}
                  checked={getIsNonEvidenceSelected}
                  onChange={handleSelectNonEvidenceAnswers}
                  disabled={getIsNonEvidenceDisabled}
                />
              )}
              <BaseCheckbox
                label={t("surveyViews.labels.evidenceAnswers")}
                checked={getIsEvidenceSelected}
                onChange={handleSelectEvidenceAnswers}
                disabled={getIsEvidenceDisabled}
              />
              <BaseCheckbox
                className="survey-view-selection-toolbar-sub-checkboxes"
                label={t("surveyViews.labels.publicEvidence")}
                checked={getIsPublicEvidenceSelected}
                onChange={handleSelectPublicEvidenceAnswers}
                disabled={getIsPublicEvidenceDisabled}
              />
              <BaseCheckbox
                className="survey-view-selection-toolbar-sub-checkboxes"
                label={t("surveyViews.labels.prmeCommunityEvidence")}
                checked={getIsPrmeCommunityEvidenceSelected}
                onChange={handleSelectPrmeCommunityEvidenceAnswers}
                disabled={getIsPrmeCommunityEvidenceDisabled}
              />
              <BaseCheckbox
                className="survey-view-selection-toolbar-sub-checkboxes"
                label={t("surveyViews.labels.privateEvidence")}
                checked={getIsPrivateEvidenceSelected}
                onChange={handleSelectPrivateEvidenceAnswers}
                disabled={getIsPrivateEvidenceDisabled}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectionToolbar;
